import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/config";
import { toast } from "sonner";
import { Loader2, Mail, Send, Gift } from "lucide-react";

/**
 * Modal to send an admin-issued intake request to a specific user.
 * Opens pre-filled with the target user's name/email; admin picks the session
 * type, optionally waives payment and adds a personal note.
 */
export default function RequestIntakeModal({ open, onOpenChange, user }) {
  const [sessionTypes, setSessionTypes] = useState([]);
  const [sessionTypeId, setSessionTypeId] = useState("");
  const [waive, setWaive] = useState(true);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get("/booking/session-types/public");
        const list = res.data || [];
        setSessionTypes(list);
        if (list.length && !sessionTypeId) {
          // Default to the "returning" session type if flagged, else first
          const def = list.find((s) => s.is_default_returning) || list[0];
          setSessionTypeId(def.session_type_id);
        }
      } catch (e) {
        toast.error("Could not load session types");
      }
    })();
    setName(user?.name || "");
    setEmail(user?.email || "");
    setMessage("");
    setWaive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.user_id]);

  const submit = async () => {
    if (!email) return toast.error("Email is required");
    if (!sessionTypeId) return toast.error("Pick a session type");
    setSending(true);
    try {
      const { data } = await api.post("/admin/intake/send-request", {
        email: email.trim(),
        name: name.trim(),
        session_type_id: sessionTypeId,
        personal_message: message.trim(),
        waive_payment: waive,
      });
      toast.success(`Intake link sent to ${data.request.email}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-xl" data-testid="request-intake-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">
            <Mail size={12} /> Admin tool
          </div>
          <DialogTitle className="font-display text-3xl mt-1">Request an intake</DialogTitle>
          <DialogDescription className="text-white/60">
            Email a prefilled intake link to this client. Link is valid for 14 days.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] tracking-[0.18em] uppercase text-white/50">Client email</Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="req-intake-email"
                className="bg-white/5 border-white/10 h-10"
                placeholder="patient@example.com"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.18em] uppercase text-white/50">Client name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="req-intake-name"
                className="bg-white/5 border-white/10 h-10"
                placeholder="Full name"
              />
            </div>
          </div>

          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-white/50">Session type</Label>
            <select
              value={sessionTypeId}
              onChange={(e) => setSessionTypeId(e.target.value)}
              data-testid="req-intake-session"
              className="w-full h-10 rounded-md bg-white/5 border border-white/10 px-3 text-sm"
            >
              {sessionTypes.map((s) => (
                <option key={s.session_type_id} value={s.session_type_id}>
                  {s.title} · {s.duration_min} min · ${(s.price_cents / 100).toFixed(0)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-white/50">
              Personal note (optional)
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="req-intake-message"
              placeholder="Hi Alex — looking forward to our chat on Thursday. Please complete this before Wednesday 5pm. — Dr. Slava"
              className="bg-white/5 border-white/10 min-h-[88px] text-sm"
              maxLength={1000}
            />
          </div>

          <label className="flex items-start gap-3 p-3 rounded-md bg-[#FF8A00]/5 border border-[#FF8A00]/20 cursor-pointer">
            <Switch
              checked={waive}
              onCheckedChange={setWaive}
              data-testid="req-intake-waive"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Gift size={14} className="text-[#FF8A00]" />
                Waive payment (complimentary intake)
              </div>
              <div className="text-[11px] text-white/55 mt-1">
                {waive
                  ? "Client skips PayPal — intake confirms instantly."
                  : "Client will be charged normally after completing the questionnaire."}
              </div>
            </div>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-white/5">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            className="bg-transparent border-white/15 hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={sending || !email || !sessionTypeId}
            data-testid="req-intake-send"
            className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-white px-6"
          >
            {sending
              ? <><Loader2 size={14} className="animate-spin mr-2" /> Sending…</>
              : <><Send size={14} className="mr-2" /> Send intake link</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
